import type { toSnakeCase as SchemaToSnakeCase } from "types/schemas";

/* eslint-disable @typescript-eslint/no-explicit-any */
type CamelCase<S extends string> = S extends `${infer H}_${infer T}`
  ? `${Lowercase<H>}${Capitalize<CamelCase<T>>}`
  : Lowercase<S>;

type SnakeCase<S extends string> = S extends `${infer H}${infer T}`
  ? H extends Lowercase<H>
    ? `${H}${SnakeCase<T>}`
    : `_${Lowercase<H>}${SnakeCase<T>}`
  : S;

export type Camelize<T> =
  T extends Array<infer U>
    ? Array<Camelize<U>>
    : T extends object
      ? { [K in keyof T as K extends string ? CamelCase<K> : K]: Camelize<T[K]> }
      : T;

export type Snakify<T> =
  T extends Array<infer U>
    ? Array<Snakify<U>>
    : T extends object
      ? { [K in keyof T as K extends string ? SnakeCase<K> : K]: Snakify<T[K]> }
      : T;

// Runtime: case conversion deep
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

const toCamelKey = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const toSnakeKey = (s: string) => s.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);

function transformKeysDeep(input: unknown, keyXform: (k: string) => string): any {
  if (Array.isArray(input)) return input.map(v => transformKeysDeep(v, keyXform));
  if (isObj(input)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      out[keyXform(k)] = transformKeysDeep(v, keyXform);
    }
    return out;
  }
  return input;
}

export const camelizeKeys = <T>(input: T): Camelize<T> => transformKeysDeep(input, toCamelKey);

export const snakifyKeys = <T>(input: T): Snakify<T> => transformKeysDeep(input, toSnakeKey);

export type MapperOptions = {
  toCamelCaseTransformers?: Record<string, (v: any) => any>;
  toSnakeCaseTransformers?: Record<string, (v: any) => any>;
};

function applyTransforms(obj: any, transforms?: Record<string, (v: any) => any>) {
  if (!transforms || !isObj(obj)) return obj;
  for (const [k, fn] of Object.entries(transforms)) {
    if (k in obj) obj[k] = fn((obj as any)[k]);
  }
  return obj;
}

export function mapToDomain<TDTO, TDomain = Camelize<TDTO>>(dto: TDTO, opts?: MapperOptions): TDomain {
  const camel = camelizeKeys(dto) as any;
  return applyTransforms(camel, opts?.toCamelCaseTransformers) as TDomain;
}

export function mapToDTO<TDomain, TDTO = Snakify<TDomain>>(domain: TDomain, opts?: MapperOptions): TDTO {
  const snake = snakifyKeys(domain) as any;
  return applyTransforms(snake, opts?.toSnakeCaseTransformers) as TDTO;
}

function transformDeep(
  input: unknown,
  keyXform: (k: string) => string,
  valuePost?: (originalKey: string, newKey: string, v: unknown) => unknown,
): any {
  if (Array.isArray(input)) return input.map(v => transformDeep(v, keyXform, valuePost));
  if (isObj(input)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      const nk = keyXform(k);
      const nv = transformDeep(v, keyXform, valuePost);
      out[nk] = valuePost ? valuePost(k, nk, nv) : nv;
    }
    return out;
  }
  return input;
}

export function toCamelCase<TIn, TOut = Camelize<TIn>>(input: TIn): TOut {
  return transformDeep(input, toCamelKey) as TOut;
}

export function toSnakeCase<TIn, TOut = SchemaToSnakeCase<TIn>>(input: TIn): TOut {
  return transformDeep(input, toSnakeKey) as TOut;
}
